import axios from 'axios';
import { ENV } from '@/config/env';

export interface StockQuote {
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  volume: number;
  high: number;
  low: number;
  open: number;
  previousClose: number;
}

/**
 * Fetch stock quote from Alpha Vantage
 */
export const getStockQuote = async (
  symbol: string
): Promise<{ data: StockQuote | null; error: string | null }> => {
  try {
    if (!ENV.ALPHA_VANTAGE_API_KEY) {
      return { data: null, error: 'Alpha Vantage API key not configured' };
    }

    const url = `${ENV.ALPHA_VANTAGE_API_URL}?function=GLOBAL_QUOTE&symbol=${symbol}&apikey=${ENV.ALPHA_VANTAGE_API_KEY}`;

    const response = await axios.get(url, { timeout: 10000 });

    const quote = response.data['Global Quote'];

    if (!quote || Object.keys(quote).length === 0) {
      return { data: null, error: `No data found for ${symbol}` };
    }

    // Parse Alpha Vantage response
    const data: StockQuote = {
      symbol: quote['01. symbol'],
      price: parseFloat(quote['05. price']),
      change: parseFloat(quote['09. change']),
      changePercent: parseFloat(quote['10. change percent'].replace('%', '')),
      volume: parseInt(quote['06. volume']),
      high: parseFloat(quote['03. high']),
      low: parseFloat(quote['04. low']),
      open: parseFloat(quote['02. open']),
      previousClose: parseFloat(quote['08. previous close']),
    };

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching stock quote:', error);
    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED') {
        return { data: null, error: 'Request timeout' };
      }
      if (error.response?.status === 429) {
        return { data: null, error: 'API rate limit exceeded' };
      }
    }
    return { data: null, error: 'Failed to fetch stock data' };
  }
};

/**
 * Fetch multiple stock quotes (batch)
 */
export const getBatchStockQuotes = async (
  symbols: string[]
): Promise<{ data: Map<string, StockQuote>; errors: string[] }> => {
  const results = new Map<string, StockQuote>();
  const errors: string[] = [];

  // Alpha Vantage free tier: 5 requests/min, so we add delay
  for (let i = 0; i < symbols.length; i++) {
    const symbol = symbols[i];

    // Add delay between requests (12 seconds = 5 per minute)
    if (i > 0) {
      await new Promise(resolve => setTimeout(resolve, 12000));
    }

    const { data, error } = await getStockQuote(symbol);

    if (error) {
      errors.push(`${symbol}: ${error}`);
      console.warn(`Failed to fetch ${symbol}:`, error);
    } else if (data) {
      results.set(symbol, data);
    }
  }

  return { data: results, errors };
};

/**
 * Search for stock symbol (for autocomplete or name lookup)
 */
export const searchStock = async (
  query: string
): Promise<{ data: any[] | null; error: string | null }> => {
  try {
    if (!ENV.ALPHA_VANTAGE_API_KEY) {
      return { data: null, error: 'Alpha Vantage API key not configured' };
    }

    const url = `${ENV.ALPHA_VANTAGE_API_URL}?function=SYMBOL_SEARCH&keywords=${query}&apikey=${ENV.ALPHA_VANTAGE_API_KEY}`;

    const response = await axios.get(url, { timeout: 10000 });

    const matches = response.data.bestMatches || [];

    return { data: matches, error: null };
  } catch (error) {
    console.error('Error searching stocks:', error);
    return { data: null, error: 'Failed to search stocks' };
  }
};

/**
 * Format price for display
 */
export const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
};

/**
 * Format change percent for display
 */
export const formatChangePercent = (percent: number): string => {
  const sign = percent >= 0 ? '+' : '';
  return `${sign}${percent.toFixed(2)}%`;
};
